import React, { useEffect, useRef, useState } from "react";
import {
  plannerClasses,
  roleLabels,
  type PlanningEntry,
} from "../shared/planner";
import icons from "virtual:planner-icons";
import "virtual:planner-themes.css";

export type SpecChoice = { className: string; spec: string };
export type PlannerDrag =
  | { type: "entry"; id: string }
  | { type: "spec"; choice: SpecChoice };

export function PlannerGlyph({
  name,
}: {
  name:
    | "edit"
    | "check"
    | "close"
    | "copy"
    | "history"
    | "link"
    | "lock"
    | "download"
    | "trash"
    | "plus";
}) {
  const paths = {
    edit: "m15 4 5 5M4 20l5-1L20 8a2 2 0 0 0-5-5L4 14z",
    check: "m5 12 4 4L19 6",
    close: "m6 6 12 12M6 18 18 6",
    copy: "M9 9h11v11H9zM15 9V4H4v11h5",
    history: "M3 5v5h5M3 10a9 9 0 1 1 1 7M12 7v5l3 2",
    link: "m10 14 4-4M8 16l-1 1a4 4 0 0 1-6-6l4-4a4 4 0 0 1 6 0M16 8l1-1a4 4 0 0 1 6 6l-4 4a4 4 0 0 1-6 0",
    lock: "M5 10h14v11H5zM8 10V6a4 4 0 0 1 8 0v4M12 14v3",
    download: "M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5",
    trash: "M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7",
    plus: "M12 5v14M5 12h14",
  };
  return (
    <svg
      className="planner-glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
}

export function SpecIcon({ className, spec }: SpecChoice) {
  const [failed, setFailed] = useState(false);
  const source = icons[className + ":" + spec];
  useEffect(() => setFailed(false), [source]);
  return (
    <span className="planner-spec-icon" aria-hidden="true">
      {source && !failed ? (
        <img
          src={source}
          alt=""
          width="36"
          height="36"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{className.slice(0, 2)}</span>
      )}
    </span>
  );
}

export function SpecPalette({
  entries,
  disabled,
  selected,
  onSelect,
  onDragStart,
  onDragEnd,
}: {
  entries: PlanningEntry[];
  disabled: boolean;
  selected: SpecChoice | null;
  onSelect: (choice: SpecChoice | null) => void;
  onDragStart: (event: React.DragEvent, choice: SpecChoice) => void;
  onDragEnd: () => void;
}) {
  return (
    <section className="planner-palette" aria-label="Class and spec palette">
      <div className="planner-palette-heading">
        <h2>Build your composition</h2>
        <p>
          Drag a spec into a slot, then add a name. Or tap a spec and its
          destination.
        </p>
      </div>
      <div className="planner-palette-grid">
        {plannerClasses.map((item) => (
          <div
            className="planner-class-palette"
            data-class={item.name}
            key={item.name}
          >
            <div className="planner-class-heading">
              <strong>{item.name}</strong>
              <span aria-label={item.name + " in raid"}>
                {
                  entries.filter((entry) => entry.className === item.name)
                    .length
                }
              </span>
            </div>
            <div className="planner-spec-options">
              {item.specs.map((spec) => {
                const choice = { className: item.name, spec };
                const active =
                  selected?.className === item.name && selected.spec === spec;
                return (
                  <button
                    key={spec}
                    type="button"
                    className="planner-spec-choice"
                    data-class={item.name}
                    data-spec={spec}
                    aria-label={"Add " + spec + " " + item.name}
                    aria-pressed={active}
                    title={spec + " " + item.name}
                    disabled={disabled}
                    draggable={!disabled}
                    onClick={() => onSelect(active ? null : choice)}
                    onDragStart={(event) => onDragStart(event, choice)}
                    onDragEnd={onDragEnd}
                  >
                    <SpecIcon {...choice} />
                    <span className="planner-spec-tooltip">{spec}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function EntryTile({
  entry,
  editable,
  selected,
  naming,
  onSelect,
  onRename,
  onRemove,
  onSaveName,
  onCancelName,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  dropTarget,
}: {
  entry: PlanningEntry;
  editable: boolean;
  selected: boolean;
  naming: boolean;
  onSelect: () => void;
  onRename: () => void;
  onRemove: () => void;
  onSaveName: (name: string) => Promise<boolean>;
  onCancelName: () => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  dropTarget: boolean;
}) {
  const placeholder = entry.name === entry.spec + " " + entry.className;
  return (
    <div
      className={
        "planner-tile" +
        (selected ? " selected" : "") +
        (naming ? " naming" : "") +
        (dropTarget ? " drop-target" : "")
      }
      data-class={entry.className}
      data-spec={entry.spec}
      draggable={editable && !naming}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {naming ? (
        <InlineName
          key={entry.id}
          entry={entry}
          disabled={!editable}
          save={onSaveName}
          close={onCancelName}
        />
      ) : (
        <>
          <button
            type="button"
            className="planner-tile-main"
            aria-pressed={selected}
            disabled={!editable}
            aria-label={
              entry.name +
              ", " +
              entry.spec +
              " " +
              entry.className +
              ", " +
              roleLabels[entry.role]
            }
            title={[
              entry.name,
              entry.spec + " " + entry.className,
              roleLabels[entry.role],
              entry.note,
            ]
              .filter(Boolean)
              .join(" | ")}
            onClick={onSelect}
          >
            <SpecIcon className={entry.className} spec={entry.spec} />
            <span className="planner-tile-text">
              <strong>{placeholder ? entry.spec : entry.name}</strong>
              <small className="planner-spec-label">
                {placeholder
                  ? entry.className
                  : entry.spec + " · " + entry.className}
              </small>
              {(entry.role !== "unspecified" || entry.note) && (
                <small className="planner-role">
                  {[
                    entry.role === "unspecified" ? "" : roleLabels[entry.role],
                    entry.note,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              )}
            </span>
          </button>
          {editable && (
            <div className="planner-tile-actions">
              <button
                type="button"
                className="planner-icon-button planner-rename"
                aria-label={"Rename " + entry.name}
                title="Rename player"
                onClick={onRename}
              >
                <PlannerGlyph name="edit" />
              </button>
              <button
                type="button"
                className="planner-icon-button planner-remove"
                aria-label={"Remove " + entry.name + " from plan"}
                title={"Remove " + entry.name + " from plan"}
                onClick={onRemove}
              >
                <PlannerGlyph name="trash" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InlineName({
  entry,
  disabled,
  save,
  close,
}: {
  entry: PlanningEntry;
  disabled: boolean;
  save: (name: string) => Promise<boolean>;
  close: () => void;
}) {
  const [name, setName] = useState(
    entry.name === entry.spec + " " + entry.className ? "" : entry.name,
  );
  const [pending, setPending] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus({ preventScroll: true });
    input.current?.select();
    input.current?.scrollIntoView({ block: "nearest" });
  }, []);
  return (
    <form
      className="planner-inline-name"
      onSubmit={(event) => {
        event.preventDefault();
        if (disabled || pending) return;
        setPending(true);
        void save(name.trim() || entry.spec + " " + entry.className)
          .then((saved) => {
            if (saved) close();
          })
          .finally(() => setPending(false));
      }}
    >
      <SpecIcon className={entry.className} spec={entry.spec} />
      <div className="planner-name-fields">
        <input
          ref={input}
          aria-label={"Player name for " + entry.spec + " " + entry.className}
          placeholder="Player name"
          value={name}
          maxLength={40}
          disabled={disabled || pending}
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
            }
          }}
        />
        <small>{entry.spec} · Enter to save</small>
      </div>
      <button
        className="planner-icon-button"
        type="submit"
        disabled={disabled || pending}
        aria-label="Save player name"
        title="Save name"
      >
        <PlannerGlyph name="check" />
      </button>
      <button
        className="planner-icon-button"
        type="button"
        disabled={pending}
        onClick={close}
        aria-label="Cancel rename"
        title="Cancel rename"
      >
        <PlannerGlyph name="close" />
      </button>
    </form>
  );
}
